import { useMemo } from "react";
import { Col, Row } from "antd";

import { Card } from "misc/card";
import { Result } from "misc/results";
import { useParams } from "misc/hooks";
import { BatterySelector, MultiSelector, Selector } from "misc/selectors";
import { datalayerUrls } from "misc/urls";
import { useSubfleets } from "misc/api/fleetconfiguration";

import ModuleSettings from "modules/ModuleSettings";
import SideHeader from "navigation/SideHeader";

import BmBatteryDetail from "./BmBatteryDetail";
import BmBatteryHealthTable from "./BmBatteryHealthTable";

const BM_COLUMN_OPTIONS = [
    { value: "batteryId", label: "t_bm_battery_id", disabled: true },
    { value: "vin", label: "t_vin", disabled: true },
    { value: "licensePlate", label: "t_license_plate" },
    { value: "batteryTypeDisplayName", label: "t_bm_battery_type" },
    { value: "lastHvrDate", label: "t_bm_last_hvr_date" },
    { value: "hvrNumber", label: "t_bm_hvr_number" },
    { value: "pmScore", label: "t_bm_pm_score" },
    { value: "pmScoreEndAt", label: "t_bm_pm_score_end_at" },
    { value: "warnings", label: "t_bm_warnings" },
    { value: "soh", label: "t_bm_soh" },
    { value: "sohEvaluatedAt", label: "t_bm_soh_evaluated_at" },
    { value: "batteryMileageKm", label: "t_bm_battery_mileage" }
];

const DEFAULT_BM_COLUMNS = [
    "batteryId",
    "vin",
    "licensePlate",
    "batteryTypeDisplayName",
    "lastHvrDate",
    "hvrNumber",
    "pmScore",
    "pmScoreEndAt",
    "warnings",
    "soh",
    "sohEvaluatedAt"
];
const DEFAULT_BM_FIXED_COLUMN = "batteryId";
const LEGACY_BM_COLUMN_NAMES = {
    batteryModelDisplayName: "batteryTypeDisplayName",
    safetyIndex: "pmScore",
    safetyIndexEndAt: "pmScoreEndAt"
};

const normalizeBmColumns = columns => [...new Set((columns ?? DEFAULT_BM_COLUMNS).map(column => LEGACY_BM_COLUMN_NAMES[column] ?? column))];

const VIEW_ITEMS = [
    {
        name: "columns",
        label: "t_module_settings_base_columns",
        render: () => <MultiSelector defaultValue={DEFAULT_BM_COLUMNS} options={BM_COLUMN_OPTIONS} maxTagCount={3} />
    },
    {
        name: "fixedColumn",
        label: "t_module_settings_fixed_column",
        render: () => (
            <Selector
                defaultValue={DEFAULT_BM_FIXED_COLUMN}
                options={[
                    { value: "batteryId", label: "t_bm_battery_id" },
                    { value: "vin", label: "t_vin" }
                ]}
            />
        )
    }
];

const BmBatteriesFleetView = () => {
    const { subfleets } = useSubfleets({ suspense: true });

    const { params, setParams } = useParams({
        options: [
            { name: "fleet", persist: "global", allowed: value => subfleets.find(fleet => fleet.subfleetId === value) },
            { name: "columns", defaultValue: DEFAULT_BM_COLUMNS, persist: "site" },
            { name: "fixedColumn", defaultValue: DEFAULT_BM_FIXED_COLUMN, persist: "site" }
        ]
    });

    const fleet = useMemo(() => subfleets.find(fleet => fleet.subfleetId === params.fleet), [params.fleet, subfleets]);
    const view = {
        columns: normalizeBmColumns(params.columns),
        fixedColumn: params.fixedColumn ?? DEFAULT_BM_FIXED_COLUMN
    };

    const exportOptions = useMemo(
        () => ({
            label: "t_bm_batteries",
            columns: [
                { value: "pmScore", label: "t_bm_pm_score" },
                { value: "batteryId", label: "t_bm_battery_id" },
                { value: "vin", label: "t_vin" },
                { value: "licensePlate", label: "t_license_plate" },
                { value: "organisationalUnit", label: "t_organisational_unit" },
                { value: "depot", label: "t_bm_depot" },
                { value: "soh", label: "t_bm_soh" },
                { value: "warning_ladeende", label: "t_bm_warning_ladeende" },
                { value: "warning_ladevorgang", label: "t_bm_warning_ladevorgang" },
                { value: "warning_temp_delta", label: "t_bm_warning_temp_delta" },
                { value: "warning_temp_range", label: "t_bm_warning_temp_range" },
                { value: "warning_temp_max", label: "t_bm_warning_temp_max" },
                { value: "pmScoreEndAt", label: "t_bm_pm_score_end_at" },
                { value: "sohEvaluatedAt", label: "t_bm_soh_evaluated_at" },
                { value: "batteryType", label: "t_bm_battery_type" },
                { value: "lastHvrDate", label: "t_bm_last_hvr_date" },
                { value: "hvrNumber", label: "t_bm_hvr_number" },
                { value: "batteryTypeCapacityKwh", label: "t_bm_usable_battery_capacity" },
                { value: "lifecycleStart", label: "t_bm_lifecycle_start" },
                { value: "currentAssignmentStartedAt", label: "t_bm_current_assignment_started_at" },
                { value: "batteryMileageKm", label: "t_bm_battery_mileage" }
            ],
            request: {
                method: "GET",
                url: datalayerUrls.fleet.widgets.list(),
                params: { type: "fleetBatteryHealthExport" },
                filter: {
                    hierarchy: fleet?.organisationalUnits,
                    vehicles: fleet?.vehicles
                }
            }
        }),
        [fleet]
    );

    return (
        <Row gutter={[10, 10]}>
            <Col span={24}>
                <ModuleSettings
                    title="t_bm_batteries"
                    values={{ fleet: params.fleet, columns: view.columns, fixedColumn: view.fixedColumn }}
                    fleetOptions={{}}
                    viewOptions={{ items: VIEW_ITEMS }}
                    exportOptions={{ export: exportOptions, range: false, disabled: !fleet }}
                    onChange={values => setParams({ ...params, ...values })}
                />
            </Col>

            {fleet ? (
                <Col span={24}>
                    <Card>
                        <BmBatteryHealthTable fleet={fleet} view={view} />
                    </Card>
                </Col>
            ) : (
                <Col span={24}>
                    <Card height={500}>
                        <Result type="noFleetSelected" />
                    </Card>
                </Col>
            )}
        </Row>
    );
};

const BmBatteriesBatteryView = () => {
    const { params, setParams } = useParams({
        options: [{ name: "battery", persist: "module" }]
    });

    return (
        <Row gutter={[10, 10]}>
            <Col span={24}>
                <Card>
                    <BatterySelector value={params.battery} onChange={battery => setParams({ ...params, battery })} />
                </Card>
            </Col>

            {params.battery ? (
                <Col span={24}>
                    <BmBatteryDetail batteryId={params.battery} />
                </Col>
            ) : (
                <Col span={24}>
                    <Card height={500}>
                        <Result type="noBatterySelected" />
                    </Card>
                </Col>
            )}
        </Row>
    );
};

const BmBatteriesComponent = () => {
    const { params, setParams } = useParams({
        options: [{ name: "view", defaultValue: "fleet-view", persist: "site" }]
    });

    return (
        <SideHeader
            value={params.view}
            options={[
                { value: "fleet-view", label: "t_view_fleet", render: () => <BmBatteriesFleetView /> },
                { value: "battery-view", label: "t_view_battery", render: () => <BmBatteriesBatteryView /> }
            ]}
            onChange={view => setParams({ ...params, view })}
        />
    );
};

export default BmBatteriesComponent;
